import { epochToLocalDate } from '../../../../shared/local-date';

/**
 * R6 (fix-parent-portal-enrollment-review 4.1): a DATE-ONLY value (birth date,
 * start dates, vaccine dates, expiry dates, school admission) is stored as
 * epoch seconds at UTC midnight. Reading it with the browser's zone shows a
 * Toronto parent the day before, so it is always rendered in UTC:
 * 1754784000 → "Aug 10, 2025".
 *
 * Timestamps (signed / submitted / agreed on) are moments, not days — keep
 * those in the parent's own zone.
 */
export function formatDateOnly(epochSeconds: number): string {
  if (!epochSeconds) return '';
  return new Date(epochSeconds * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * R6 (4.1) the input side: a date-only epoch is a UTC day, so the yyyy-mm-dd a
 * date control holds must come from the UTC parts too. Reading it with
 * `epochToLocalDate` shows a Toronto parent the day before — and the form then
 * writes that day back as UTC midnight, shifting the value on every save.
 */
export function epochToUtcDateString(epochSeconds: number): string {
  if (!epochSeconds) return '';
  const d = new Date(epochSeconds * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export class DateUtils {
  /** R6: the shared date-only formatter (see `formatDateOnly` above). */
  static formatDateOnly(epochSeconds: number): string {
    return formatDateOnly(epochSeconds);
  }

  static currentDateTime(): number {
    return Math.floor(new Date().getTime() / 1000);
  }

  static currentDay(): number {
    return Math.floor(new Date().getTime() / 1000);
  }

  static dateToEpoch(date: string | Date): number {
    const epochDate = Math.floor(new Date(date).getTime() / 1000);
    return isNaN(epochDate) ? 0 : epochDate;
  }

  static epochToDate(epoch: number): Date {
    return new Date(epoch * 1000);
  }

  static epochToDateString(epoch: number): string {
    return epochToLocalDate(epoch);
  }

  static today(epoch?: number): number {
    return this.epochDay();
  }

  static epochDay(
    day = new Date().getDate(),
    month = new Date().getMonth(),
    year = new Date().getFullYear(),
  ): number {
    return new Date(year, month, day, 0, 0, 0, 0).getTime() / 1000;
  }

  static calculateAge(birthDateEpoch: number): number {
    const today = new Date().getFullYear();
    const age = new Date(birthDateEpoch * 1000).getFullYear();
    return today - age;
  }
  static daysBetweenDates(epoch1: number, epoch2: number): number {
    const Time =
      this.epochToDate(epoch2).getTime() - this.epochToDate(epoch1).getTime();
    const Days = Time / (1000 * 3600 * 24);
    return Math.round(Days + 1);
  }

  static convertTwelveHour(timeString: string): string {
    const H = +timeString.substr(0, 2);
    const h = H % 12 || 12;
    const ampm = H < 12 ? 'AM' : 'PM';
    timeString = h + timeString.substr(2, 3) + ampm;
    return timeString;
  }

  static getStartOfWeek() {
    const current = new Date(); // get current date
    current.setHours(0, 0, 0, 0);
    const weekstart = current.getDate() - current.getDay() + 1;
    const monday = new Date(current.setDate(weekstart));
    return monday;
  }

  static getDaysInRange(epoch1: number, epoch2: number) {
    const daysInRange: number[] = [];
    let currentDay: number = epoch1;

    while (currentDay <= epoch2) {
      daysInRange.push(currentDay);
      currentDay = currentDay + 86400;
    }
    return daysInRange;
  }

  static getEndOfWeek() {
    const current = new Date();
    current.setHours(0, 0, 0, 0);
    const weekstart = current.getDate() - current.getDay() + 1;
    const weekend = weekstart + 4; // end day is the first day + 4

    const friday = new Date(current.setDate(weekend));
    return friday;
  }
  static epochToUTCDate(epoch: number): Date {
    const d = new Date(epoch * 1000);
  
    return new Date(
      Date.UTC(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate(),
        12, // noon prevents timezone shift
        0,
        0
      )
    );
  }
  // static dateToUTCEpoch(date: Date): number {
  //   return Math.floor(
  //     Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 1000,
  //   );
  // }
 

  static epochToReadableDateTime(epoch: number): string {
    if (!epoch || epoch === 0) {
      return '';
    }
    const date = new Date(epoch * 1000);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  static epochToReadableDateTimeUTC(epoch: number): string {
    if (!epoch || epoch === 0) {
      return '';
    }
    const date = new Date(epoch * 1000);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    });
  }

  static dateToUTCEpoch(date: Date): number {
    return Math.floor(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0) /
        1000,
    );
  }
}
